import { useLoading } from "../context/LoadingContext";

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaSearch, FaEdit, FaTrash } from "react-icons/fa";
import GenericModal from "../components/GenericModal";
import EditFornecedorModal from "../components/EditFornecedorModal";
import { useUser } from "../context/UserContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../css/ListarFornecedores.css"; // Import the CSS file
import "../css/EditFornecedorModal.css"; // Import the CSS file for the edit modal
import BackButton from "../components/BackButton";

const ListarFornecedores = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [nomeFornecedor, setNomeFornecedor] = useState("");
  const [fornecedores, setFornecedores] = useState([]);
  const { setIsLoading } = useLoading();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentFornecedor, setCurrentFornecedor] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [fornecedorToDelete, setFornecedorToDelete] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newFornecedorData, setNewFornecedorData] = useState({
    nome: "",
    responsavel: "",
    contato: "",
    email: "",
  });

  const handleBack = () => {
    navigate(`/restaurantes`);
  };

  const handleSearch = async () => {
    const toastId = toast.loading("Buscando fornecedores...");
    setIsLoading(true);
    try {
      const user = sessionStorage.getItem("user");
      const { access_token } = JSON.parse(user);

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/v1/fornecedores?restauranteId=${id}&nome=${nomeFornecedor}&pageNumber=0&pageSize=10`,
        {
          headers: {
            "Content-Type": "application/json",
            Authentication: access_token,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setFornecedores(data || []); // Assuming the API returns a 'content' array
      toast.update(toastId, {
        render: "Busca concluída!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      setFornecedores([]); // Clear suppliers on error
      toast.update(toastId, {
        render: "Erro ao buscar fornecedores.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (fornecedor) => {
    setCurrentFornecedor(fornecedor);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentFornecedor(null);
  };

  const handleDeleteClick = (fornecedor) => {
    setFornecedorToDelete(fornecedor);
    setShowDeleteConfirmModal(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmModal(false);
    setFornecedorToDelete(null);
  };

  const handleConfirmDelete = async () => {
    const toastId = toast.loading("Excluindo fornecedor...");
    setIsLoading(true);
    try {
      const user = sessionStorage.getItem("user");
      const { access_token } = JSON.parse(user);

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/v1/fornecedores/${fornecedorToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authentication: access_token,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.update(toastId, {
        render: "Fornecedor excluído com sucesso!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });
      handleSearch(); // Re-fetch the list of suppliers
      handleCancelDelete();
    } catch (error) {
      console.error("Error deleting supplier:", error);
      toast.update(toastId, {
        render: "Erro ao excluir fornecedor.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFornecedor = async (updatedFornecedor) => {
    const toastId = toast.loading("Salvando fornecedor...");
    setIsLoading(true);
    try {
      const user = sessionStorage.getItem("user");
      const { access_token } = JSON.parse(user);

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/v1/fornecedores/${updatedFornecedor.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authentication: access_token,
          },
          body: JSON.stringify(updatedFornecedor),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.update(toastId, {
        render: "Fornecedor salvo com sucesso!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });
      handleSearch();
      handleCloseModal();
    } catch (error) {
      console.error("Error updating supplier:", error);
      toast.update(toastId, {
        render: "Erro ao salvar fornecedor.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewFornecedorChange = (e) => {
    const { name, value } = e.target;
    setNewFornecedorData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setNewFornecedorData({
      nome: "",
      responsavel: "",
      contato: "",
      email: "",
    });
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const toastId = toast.loading("Criando fornecedor...");
    setIsLoading(true);

    try {
      const user = sessionStorage.getItem("user");
      const { access_token } = JSON.parse(user);

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/v1/fornecedores?restauranteId=${id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authentication: access_token,
          },
          body: JSON.stringify(newFornecedorData),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.update(toastId, {
        render: "Fornecedor criado com sucesso!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });
      handleSearch(); // Re-fetch the list of suppliers
      handleCancelCreate(); // Hide the form and reset data
    } catch (error) {
      console.error("Error creating supplier:", error);
      toast.update(toastId, {
        render: "Erro ao criar fornecedor.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleSearch();
  }, []);

  return (
    <div className="fornecedores-container">
      <BackButton inline />
      <h1>Listagem de Fornecedores</h1>
      
      <div className="filtro-container">
        <div className="periodo-selecionado">
          <span>Nome do Fornecedor: </span>
        </div>
        <div className="filtro-data-group">
         <div className="filtro-data">
            <FaSearch className="filtro-icon" />
            <input
              type="text"
              value={nomeFornecedor}
              onChange={(e) => setNomeFornecedor(e.target.value)}
              placeholder="Buscar por nome"
              className="date-picker-input"
            />
          </div>
        </div>
        <div className="botoes-superiores">
          <button
            className="btn-adicionar"
            onClick={handleSearch}
            disabled={false}
          >
            Buscar
          </button>
        </div>
      </div>

      <div className="botoes-superiores">
        <button
          className="btn-novo"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? "Cancelar Cadastro" : "Criar Novo Fornecedor"}
        </button>
      </div>

      {showCreateForm && (
        <div className="form-nova-fornecedores">
          <h3>Cadastrar Novo Fornecedor</h3>
          <form onSubmit={handleCreateSubmit}>
            <div className="form-group">
              <label>
                Nome do Fornecedor:
                <input
                  type="text"
                  name="nome"
                  value={newFornecedorData.nome}
                  onChange={handleNewFornecedorChange}
                  required
                />
              </label>
            </div>
            <div className="form-group">
              <label>
                Nome do Responsável:
                <input
                  type="text"
                  name="responsavel"
                  value={newFornecedorData.responsavel}
                  onChange={handleNewFornecedorChange}
                  required
                />
              </label>
            </div>
            <div className="form-group">
              <label>
                Número de Contato:
                <input
                  type="text"
                  name="contato"
                  value={newFornecedorData.contato}
                  onChange={handleNewFornecedorChange}
                  required
                />
              </label>
            </div>
            <div className="form-group">
              <label>
                Email:
                <input
                  type="email"
                  name="email"
                  value={newFornecedorData.email}
                  onChange={handleNewFornecedorChange}
                  required
                />
              </label>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                onClick={handleCancelCreate}
                className="btn-secundario"
              >
                Cancelar
              </button>
              <button type="submit" className="btn-primario" disabled={false}>
                {false ? "Salvando..." : "Salvar Fornecedor"}
              </button>
            </div>
          </form>
        </div>
      )}

      {false ? (
        <p className="loading-spinner">Carregando fornecedores...</p>
      ) : (
        <div className="fornecedores-grid">
          {fornecedores.length > 0 ? (
            <ul>
              {fornecedores.map((fornecedor) => (
                <li key={fornecedor.id} className="fornecedores-card">
                  <h3>{fornecedor.nome}</h3>
                  <p>
                    <strong>Responsável:</strong> {fornecedor.nomeResponsavel}
                  </p>
                  <p>
                    <strong>Contato:</strong> {fornecedor.telefone}
                  </p>
                  <p>
                    <strong>Email:</strong> {fornecedor.email}
                  </p>
                  <div className="edit-delete-icons">
                    <div
                      className="edit-icon"
                      onClick={() => handleEditClick(fornecedor)}
                    >
                      <FaEdit />
                    </div>
                    <div
                      className="delete-icon"
                      onClick={() => handleDeleteClick(fornecedor)}
                    >
                      <FaTrash />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="sem-fornecedores">Nenhum fornecedor encontrado.</p>
          )}
        </div>
      )}

      <GenericModal isOpen={isModalOpen} onClose={handleCloseModal}>
        {currentFornecedor && (
          <EditFornecedorModal
            fornecedor={currentFornecedor}
            onSave={handleSaveFornecedor}
            onCancel={handleCloseModal}
          />
        )}
      </GenericModal>

      <GenericModal
        isOpen={showDeleteConfirmModal}
        onClose={handleCancelDelete}
      >
        <div className="modal-content">
          <h2>Confirmar Exclusão</h2>
          <p>
            Tem certeza que deseja excluir o fornecedor "
            {fornecedorToDelete?.nome}"?
          </p>
          <div className="modal-actions">
            <button onClick={handleCancelDelete} className="btn-secundario">
              Cancelar
            </button>
            <button
              onClick={handleConfirmDelete}
              className="btn-primario delete-button"
            >
              Excluir
            </button>
          </div>
        </div>
      </GenericModal>
      <ToastContainer />
    </div>
  );
};

export default ListarFornecedores;